import { router } from '@inertiajs/react'
import { useState, useRef, useCallback } from 'react'
import {
  Search,
  X,
  AlertTriangle,
  Loader2,
} from 'lucide-react'
import { getInitials, getAvatarColor } from '@/lib/displayUtils'

interface ContactSummary {
  id: number
  name: string | null
  email: string | null
  phone: string | null
}

interface PrimaryContact {
  id: number
  displayName: string
  email: string | null
  phone: string | null
}

interface MergeContactModalProps {
  primary: PrimaryContact
  onClose: () => void
}

function formatContactDetail(contact: ContactSummary | PrimaryContact): string {
  return [contact.email, contact.phone].filter(Boolean).join(' \u00b7 ') || `ID: ${contact.id}`
}

export default function MergeContactModal({ primary, onClose }: MergeContactModalProps) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<ContactSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [target, setTarget] = useState<ContactSummary | null>(null)
  const [merging, setMerging] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearch = useCallback((query: string) => {
    setSearch(query)
    setTarget(null)

    if (searchTimeout.current) clearTimeout(searchTimeout.current)

    if (query.trim().length < 2) {
      setResults([])
      return
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await fetch(`/contacts/search?q=${encodeURIComponent(query.trim())}`)
        const data = await response.json()
        setResults(
          (data.users || []).filter((u: { id: number }) => u.id !== primary.id)
        )
      } finally {
        setSearching(false)
      }
    }, 300)
  }, [primary.id])

  function handleConfirm(): void {
    if (!target) return
    setMerging(true)
    router.post(`/contacts/${primary.id}/merge`, { secondary_id: target.id }, {
      onFinish: () => setMerging(false),
      onSuccess: () => onClose(),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Mesclar contato</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {!target ? (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Busque o contato que será mesclado em <strong>{primary.displayName}</strong>.
                O contato selecionado será excluído e todos os seus dados transferidos.
              </p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Buscar por nome, e-mail ou telefone..."
                  autoFocus
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>

              <div className="max-h-60 overflow-y-auto">
                {results.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {results.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => setTarget(user)}
                        className="w-full text-left p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(user.name || user.email || 'U')}`}>
                            {getInitials(user.name || user.email || 'U')}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {user.name || 'Sem nome'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {formatContactDetail(user)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {results.length === 0 && search.trim().length >= 2 && !searching && (
                  <p className="text-sm text-gray-500 text-center py-4">Nenhum contato encontrado</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Esta ação é irreversível. O contato secundário será excluído permanentemente.
                </p>
              </div>

              <div className="space-y-3">
                <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Contato principal (mantido)</p>
                  <p className="text-sm font-medium text-gray-900">{primary.displayName}</p>
                  <p className="text-xs text-gray-500">{formatContactDetail(primary)}</p>
                </div>

                <div className="p-3 border border-red-200 bg-red-50 rounded-lg">
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Contato secundário (será excluído)</p>
                  <p className="text-sm font-medium text-gray-900">{target.name || 'Sem nome'}</p>
                  <p className="text-xs text-gray-500">{formatContactDetail(target)}</p>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setTarget(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Voltar
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={merging}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {merging ? 'Mesclando...' : 'Confirmar merge'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
